import { useState } from 'react';
import { createContainer } from 'unstated-next';
import { Store, useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { CustomerTypeModel } from './CustomerTypeInput';
import { DeliveryAddressModel } from './DeliveryAddressInput';

export const phonePattern = '^[0-9-+() ]{0,32}$';

const regexPatterns: Record<
	Store,
	{
		org: string;
		identifier: string;
		phonePattern: string;
		zip: string;
	}
> = {
	kgno: {
		org: '^(?=[^0\\s])(?:\\d\\s*){9}$', // 9 digits, no leading 0, optional spaces
		identifier: '^(0192:)?[0-9]{9}$',
		phonePattern,
		zip: '',
	},
	kgse: {
		org: '^([0-9-+]{10,15})$',
		identifier: '^(0007:)?[0-9]{10}$|^(0088:)?[0-9]{13}$',
		phonePattern,
		zip: '^(?:\\s*\\d\\s*){5}$',
	},
	kgdk: {
		org: '^(?=[^0\\s])(?:\\d\\s*){8}$', // 8 digits, no leading 0, optional spaces
		identifier: '^(0007:)?[0-9]{8}$|^(0088:)?[0-9]{13}$',
		phonePattern,
		zip: '',
	},
	kgat: {
		org: '^([0-9-+]{8,15})$',
		identifier: '^(0007:)?[0-9]{8}$|^(0088:)?[0-9]{13}$',
		phonePattern,
		zip: '',
	},
	kgde: {
		org: '^([a-zA-Z0-9-+\\s]{6,15})$',
		identifier: '^(0007:)?[0-9]{8}$|^(0088:)?[0-9]{13}$',
		phonePattern,
		zip: '',
	},
	kgnl: {
		org: '^(?=[^0\\s])(?:\\d\\s*){8}$', // 8 digits, no leading 0, optional spaces
		identifier: '',
		phonePattern,
		zip: '',
	},

	kggb: {
		org: '^([0-9-+]{8,15})$',
		identifier: '^(0007:)?[0-9]{8}$|^(0088:)?[0-9]{13}$',
		phonePattern,
		zip: '',
	},
};

interface ValidationResponse {
	isValid: boolean;
	type: 'error' | 'warning' | 'success';
	message: string;
}

const defaultValidation: ValidationResponse = {
	isValid: true,
	type: 'success',
	message: '',
};

interface UseFieldValidationProps {
	name: string;
	rgxPattern?: string;
	warningMessage?: string;
	errorMessage?: string;
	isRequired?: boolean;
	invalidateOnRegexFail?: boolean;
	postRgxValidation?: (validationData?: Record<string, any>) => boolean;
	postRgxValidationErrorMessage?: string;
}

function useFieldValidation({
	name,
	rgxPattern,
	warningMessage,
	errorMessage,
	isRequired = true,
	invalidateOnRegexFail = false,
	postRgxValidation,
	postRgxValidationErrorMessage,
}: UseFieldValidationProps) {
	const f = useTranslation().form;
	const [validation, setValidation] = useState<ValidationResponse>(defaultValidation);
	const [value, setValue] = useState<string>('');

	function validate(
		value: string,
		name: string,
		pattern?: string,
		validationData?: Record<string, any>
	): ValidationResponse {
		if (!isRequired) return { isValid: true, type: 'success', message: '' };

		if (!value) return { isValid: false, type: 'error', message: errorMessage ?? `${name} ${f.required}` };
		if (pattern) {
			const regex = new RegExp(pattern);
			if (!regex.test(value)) {
				const message = errorMessage ? errorMessage : warningMessage ? warningMessage : `${name} ${f.invalid}`;
				return {
					isValid: invalidateOnRegexFail ? false : true,
					type: invalidateOnRegexFail ? 'error' : 'warning',
					message: message,
				};
			}

			if (typeof postRgxValidation !== 'undefined' && !postRgxValidation(validationData)) {
				return {
					isValid: false,
					message: postRgxValidationErrorMessage ?? `${name} ${f.invalid}`,
					type: 'error',
				};
			}
		}
		return { isValid: true, type: 'success', message: '' };
	}

	function validateWrapper(shouldSetState = true, validationData?: Record<string, any>) {
		const res = validate(value, name, rgxPattern, validationData);
		if (shouldSetState) {
			setValidation(res);
		}
		return res;
	}

	function setValueAndValidate(value: string) {
		setValue(value);
		const res = validate(value, name, rgxPattern);
		setValidation(res);
	}

	function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
		setValue(e.target.value);
	}

	return {
		...validation,
		isRequired,
		validate: validateWrapper,
		value,
		setValue,
		handleOnChange,
		setValueAndValidate,
	};
}

function useField() {
	const [value, setValue] = useState<string>('');

	function handleOnChange(e: React.ChangeEvent<HTMLInputElement>) {
		setValue(e.target.value);
	}

	return { value, setValue, handleOnChange };
}

function useValidation() {
	const f = useTranslation().form;
	const tAccount = useTranslation().accountPage;
	const config = useConfig();
	const rgxPattern = regexPatterns[config.store];

	// Delivery
	const deliveryPhone = useFieldValidation({
		name: f.phone,
		rgxPattern: rgxPattern.phonePattern,
		invalidateOnRegexFail: false,
		postRgxValidationErrorMessage: 'Ogiltigt mobilnummer',
		postRgxValidation: (_validationData) => {
			return true;
			/* 			if (config.store === 'kgse' && validationData && validationData.deliveryType !== 'door_delivery') {
				const isMobilePhoneNumber = (deliveryPhoneInput: string): boolean => {
					const numericPhoneNumber = deliveryPhoneInput.replace(/[^0-9]/g, '');
					const mobileNumberPatterns = ['07', '7', '00467', '0467', '467'];
					for (const pattern of mobileNumberPatterns) {
						if (numericPhoneNumber.startsWith(pattern)) return true;
					}
					return false;
				};
				return isMobilePhoneNumber(deliveryPhone.value);
			}
			return true; */
		},
	});
	const deliveryCompany = useFieldValidation({ name: tAccount.companyName });
	const deliveryOrgnr = useFieldValidation({ name: f.organisationNumber, rgxPattern: rgxPattern.org });
	const deliveryStreet = useFieldValidation({ name: f.street });
	const deliveryCity = useFieldValidation({ name: f.city });
	const deliveryZip = useFieldValidation({
		name: f.zip,
		isRequired: true,
		rgxPattern: rgxPattern.zip,
		invalidateOnRegexFail: true,
	});
	const deliveryCareOf = useField();
	const deliveryDoorCode = useField();
	const deliveryFirstname = useField();
	const deliveryLastname = useField();
	const comment = useField();

	// Payment
	const paymentType = useField();
	const paymentEmail = useFieldValidation({
		name: f.email,
		isRequired: ['pdf', 'ff'].includes(paymentType.value),
	});
	const paymentOrgnr = useFieldValidation({
		name: f.organisationNumber,
		rgxPattern: rgxPattern.org,
		// kgde still asks for the company number on these payment methods, but does not require it
		isRequired: ['pdf', 'einvoice', 'paper'].includes(paymentType.value) && config.store !== 'kgde',
	});
	const paymentCity = useFieldValidation({ name: f.city });
	const paymentStreet = useFieldValidation({
		name: f.street,
		isRequired: ['pdf', 'ff', 'einvoice', 'paper'].includes(paymentType.value),
	});
	const paymentCompany = useFieldValidation({
		name: tAccount.companyName,
		isRequired: ['pdf', 'ff', 'einvoice', 'paper'].includes(paymentType.value),
	});
	const paymentZip = useFieldValidation({
		name: f.zip,
		isRequired: true,
	});
	const paymentCard = useFieldValidation({
		name: f.card,
		isRequired: paymentType.value === 'card',
		errorMessage: f.cardInfoIsMissing,
	});
	const paymentRef1 = useFieldValidation({
		name: f.reference,
		isRequired: paymentType.value === 'einvoice' && config.store === 'kgse',
	});
	const paymentIdentifier = useFieldValidation({
		name: f.einvoiceIdentifier,
		rgxPattern: rgxPattern.identifier,
		isRequired: paymentType.value === 'einvoice' && config.store === 'kgse',
	});
	const paymentOperator = useFieldValidation({
		name: f.einvoiceOperator,
		isRequired: paymentType.value === 'einvoice' && config.store === 'kgse',
	});
	const paymentCareOf = useField();

	function validatePaymentMethod(shouldSetState = true, customerType?: CustomerTypeModel['customerType']) {
		const validatedEmail = paymentEmail.validate(shouldSetState);
		const validatedStreet = paymentStreet.validate(shouldSetState);
		const validatedCompany = paymentCompany.validate(shouldSetState);
		const validatedOrgnr: ValidationResponse =
			customerType === 'private'
				? { isValid: true, type: 'success', message: '' }
				: paymentOrgnr.validate(shouldSetState);
		const validatedZip = paymentZip.validate(shouldSetState);
		const validatedInvoiceOperator = paymentOperator.validate(shouldSetState);
		const validatedInvoiceIdentifier = paymentIdentifier.validate(shouldSetState);
		const validatedRef1 = paymentRef1.validate(shouldSetState);

		return (
			validatedEmail.isValid &&
			validatedStreet.isValid &&
			validatedCompany.isValid &&
			validatedOrgnr.isValid &&
			validatedZip.isValid &&
			validatedInvoiceOperator.isValid &&
			validatedInvoiceIdentifier.isValid &&
			validatedRef1.isValid
		);
	}

	function validateDeliveryAddress(validationData?: Record<string, any>) {
		const validatedCompany = deliveryCompany.validate();
		const validatedStreet = deliveryStreet.validate();
		const validatedCity = deliveryCity.validate();
		const validatedPhone = deliveryPhone.validate(true, validationData);
		const validatedZip = deliveryZip.validate();

		return (
			validatedCompany.isValid &&
			validatedStreet.isValid &&
			validatedCity.isValid &&
			validatedPhone.isValid &&
			validatedZip.isValid
		);
	}

	function fillAddressForm({
		firstname,
		lastname,
		company,
		careOf,
		street,
		zip,
		city,
		doorCode,
		phone,
	}: DeliveryAddressModel) {
		deliveryPhone.setValueAndValidate(phone);
		deliveryCompany.setValueAndValidate(company);
		deliveryStreet.setValueAndValidate(street);
		deliveryCity.setValueAndValidate(city);
		deliveryZip.setValueAndValidate(zip);
		deliveryCareOf.setValue(careOf);
		deliveryDoorCode.setValue(doorCode);
		deliveryFirstname.setValue(firstname);
		deliveryLastname.setValue(lastname);
	}

	function resetAddressForm() {
		deliveryPhone.setValue('');
		deliveryCompany.setValue('');
		deliveryStreet.setValue('');
		deliveryCity.setValue('');
		deliveryZip.setValue('');
		deliveryCareOf.setValue('');
		deliveryDoorCode.setValue('');
		deliveryFirstname.setValue('');
		deliveryLastname.setValue('');
	}

	return {
		validatePaymentMethod,
		validateDeliveryAddress,
		resetAddressForm,
		fillAddressForm,

		paymentCard,
		paymentEmail,
		paymentOrgnr,
		paymentCity,
		paymentStreet,
		paymentCompany,
		paymentIdentifier,
		paymentZip,
		paymentRef1,
		paymentOperator,
		paymentType,
		paymentCareOf,

		deliveryPhone,
		deliveryCompany,
		deliveryOrgnr,
		deliveryStreet,
		deliveryCity,
		deliveryZip,
		deliveryCareOf,
		deliveryDoorCode,
		deliveryFirstname,
		deliveryLastname,
		comment,
	};
}

export const FormValidationContainer = createContainer(useValidation);

export function useFormValidationContainer() {
	return FormValidationContainer.useContainer();
}
